/**
 * @name useIdentificacaoAluno
 * @author Gabriel M. Guimarães                // 1. Primeira tentativa: RFID com carregamento de produtos
               console.log('🔍 Tentativa 1: RFID');
               let aluno = await identificarECarregarProdutos(valorLimpo, 'rfid');
               
               // 2. Segunda tentativa: Matrícula (apenas se não encontrou por RFID)
               if (!aluno) {
                    console.log('🔍 Tentativa 2: Matrícula');
                    aluno = await identificarECarregarProdutos(valorLimpo, 'codigo');
               } else {
                    console.log('✅ Encontrado por RFID - produtos carregados com restrições');
               }7
 * @description Hook para identificação de alunos com busca sequencial simples:
 * 1. Tenta RFID primeiro
 * 2. Se não encontrar, tenta matrícula
 * 3. Se não encontrar, mostra erro "Aluno não encontrado"
 * @returns {UseIdentificacaoAlunoReturn} Estados e handlers para identificação
 * @package src/components/pages/IdentificacaoAluno/useIdentificacaoAluno.ts
 */

import { useAlert } from "@/hooks/useAlert";
import { useAlunoComProdutos } from "@/hooks/useAlunoComProdutos";
import { useDataIntegrityChecker } from "@/hooks/useDataIntegrityChecker";
import { removeLeadingZeros } from "@/utils/rfidUtils";
import { useCallback, useState } from "react";

/**
 * Interface de retorno do hook
 */
export interface UseIdentificacaoAlunoReturn {
  // Estados
  identificador: string;
  isCarregando: boolean;

  // Handlers
  setIdentificador: (valor: string) => void;
  handleIdentificar: () => Promise<void>;
  handleKeyPress: (e: React.KeyboardEvent) => void;

  // Metadados estáticos
  placeholder: string;
  label: string;
  instrucoes: {
    titulo: string;
    subtitulo: string;
  };
}

/**
 * Hook para identificação sequencial de alunos
 * Busca simples: RFID → matrícula → erro
 * IMPORTANTE: Após identificação, recarrega produtos com restrições do aluno
 */
export const useIdentificacaoAluno = (): UseIdentificacaoAlunoReturn => {
  const { identificarECarregarProdutos } = useAlunoComProdutos();
  const { showError } = useAlert();
  const { verificarIntegridadeDados } = useDataIntegrityChecker();

  // Estados locais
  const [identificador, setIdentificador] = useState("");
  const [isCarregando, setIsCarregando] = useState(false);

  /**
   * Handler customizado para definir identificador SEM processamento automático
   * O processamento de zeros à esquerda agora só acontece na identificação final
   */
  const handleSetIdentificador = useCallback((valor: string) => {
    // ⚡ CORREÇÃO: Não processa zeros à esquerda aqui
    // Deixa o valor bruto para permitir captura completa do RFID
    console.log("🔧 Valor recebido no identificador (sem processamento):", {
      valor: `"${valor}"`,
      tamanho: valor.length,
      timestamp: new Date().toISOString(),
    });

    setIdentificador(valor);
  }, []);

  /**
   * Função principal de identificação com busca sequencial
   * 1. Busca por RFID primeiro
   * 2. Se não encontrar, busca por matrícula
   * 3. Se não encontrar, exibe erro
   */
  const handleIdentificar = useCallback(async () => {
    // Processa o valor removendo zeros à esquerda antes de usar
    const valorOriginal = identificador.trim();
    const valorProcessado = removeLeadingZeros(valorOriginal);

    // ⚡ DEBUG CRÍTICO - Verificar processamento de valores
    console.log("🔧 DEBUG CRÍTICO - Processamento de valor:", {
      identificadorEstado: identificador,
      valorOriginal: valorOriginal,
      valorProcessado: valorProcessado,
      tamanhoOriginal: valorOriginal.length,
      tamanhoProcessado: valorProcessado.length,
      tipoOriginal: typeof valorOriginal,
      tipoProcessado: typeof valorProcessado,
      ehVazio: !valorProcessado,
      ehZero: valorProcessado === "0",
      timestamp: new Date().toISOString(),
    });

    if (!valorProcessado) {
      console.log("❌ Valor processado está vazio - abortando identificação");
      return;
    }

    try {
      setIsCarregando(true);
      console.log("🔄 Iniciando busca sequencial:", {
        valorOriginal: valorOriginal,
        valorProcessado: valorProcessado,
        timestamp: new Date().toISOString(),
      });

      // 1. Primeira tentativa: RFID
      console.log("🔍 Tentativa 1: RFID com valor:", valorProcessado);
      let aluno = await identificarECarregarProdutos(valorProcessado, "rfid");

      // 2. Segunda tentativa: Matrícula (apenas se não encontrou por RFID)
      if (!aluno) {
        console.log("🔍 Tentativa 2: Matrícula");
        aluno = await identificarECarregarProdutos(valorProcessado, "codigo");
      } else {
        console.log("✅ Encontrado por RFID");
      }

      // 3. Log do resultado final
      if (aluno) {
        console.log("✅ Aluno identificado com sucesso");

        // TC05: Verificar integridade dos dados após identificação
        setTimeout(async () => {
          console.log("🔍 Verificando integridade dos dados...");
          await verificarIntegridadeDados();
        }, 1000); // Aguarda 1 segundo para garantir que outros dados carregaram
      } else {
        console.log("❌ Aluno não encontrado em nenhuma busca");
        // Só mostra erro aqui, após tentar ambas as opções
        showError("Aluno não encontrado");
      }
    } catch (error) {
      console.error("❌ Erro na busca sequencial:", error);
      showError("Erro ao identificar aluno");
    } finally {
      setIdentificador(""); // Limpa o campo sempre
      setIsCarregando(false);
    }
  }, [
    identificador,
    identificarECarregarProdutos,
    showError,
    verificarIntegridadeDados,
  ]);

  /**
   * Handler para tecla Enter
   */
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        handleIdentificar();
      }
    },
    [handleIdentificar],
  );

  return {
    // Estados
    identificador,
    isCarregando,

    // Handlers
    setIdentificador: handleSetIdentificador, // Usa o handler que remove zeros à esquerda
    handleIdentificar,
    handleKeyPress,

    // Metadados estáticos
    placeholder: "RFID ou matrícula do aluno...",
    label: "Identificação do Aluno",
    instrucoes: {
      titulo: "📱 Aproxime seu cartão RFID ou digite sua matrícula",
      subtitulo: "O sistema tentará localizar você automaticamente",
    },
  };
};

export default useIdentificacaoAluno;
